import { observer } from 'mobx-react-lite'
import { type UIEvent, useEffect, useRef, useState, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { InView } from 'react-intersection-observer'
import { debounce } from 'lodash'
import { SpinnerLoader } from 'shared/ui'
import useIsTabActive from 'shared/hooks/useIsTabActive'
import { getAriaLabel } from 'shared/lib'
import { ScrollToBottomBtn } from 'shared/ui/ScrollToBottomBtn/ScrollToBottomBtn'
import { type Activity } from 'entities/Activity/model/Activity'
import { type Message } from 'entities/Message/model/Message'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { MessageCard } from 'widgets/MessageCard'
import { DayContent } from 'widgets/DayContent/DayContent'
import { useEventMessageTranscriptCompleted } from 'widgets/ConversationMessages/events/EventMessageTranscriptCompleted'
import { useConversationMessagesContext } from 'widgets/ConversationMessages/context'
import { useEventMessageUpdatedNew } from 'widgets/ConversationMessages/events/EventMessageUpdatedNew'
import { useEventMessageReceivedNew } from 'widgets/ConversationMessages/events/EventMessageReceivedNew'
import { useEventVoicemailReceived } from 'widgets/ConversationMessages/events/EventVoicemailReceived'
import { useEventConversationActivityCreated } from 'widgets/ConversationMessages/events/EventConversationActivityCreated'
import { useEventTyping } from 'widgets/ConversationMessages/events/EventTyping'
import { useEventViewing } from 'widgets/ConversationMessages/events/EventViewing'
import { EmptyState } from 'widgets/ConversationMessages/ui/ConversationContent/ui/EmptyState/EmptyState'
import { useEventMessageTranscriptStarted } from 'widgets/ConversationMessages/events/EventMessageTranscriptStarted'
import { useEventMessageTranscriptFailed } from 'widgets/ConversationMessages/events/EventMessageTranscriptFailed'
import styles from './styles.module.scss'

const SHOW_ARROW_BOTTOM = 10

export const ConversationContent = observer(() => {
  const { conversationStore } = useEntitiesContext()
  const store = useConversationMessagesContext()
  const [searchParams] = useSearchParams()
  const {
    scrollBottomTrigger,
    hasPrevious,
    loading,
    sendingMessages,
    callContentStore,
    setScrollBottomTrigger,
    loadPreviousMessages,
    setIsActiveTab,
    setHideAvatars,
    scheduledItems,
    ordinaryItems,
    isUnread,
    setIsBottomFixed,
    messagesContentScrollHeight,
    setMessagesContentScrollHeight,
    mediaLoadedTrigger,
    messageId: storeMessageId,
    loadingOverlay,
  } = store
  const refWrap = useRef<HTMLDivElement>(null)
  const [visibleArrowBottom, setVisibleArrowBottom] = useState(false)
  const [hasScrolledToMessage, setHasScrolledToMessage] = useState(false)
  const messageId = storeMessageId || searchParams.get('messageId')

  const debouncedScrollToMessage = useCallback(
    debounce((scrollToMessage: () => void) => {
      setHasScrolledToMessage(true)
      scrollToBottom()
      scrollToMessage()
    }, 500),
    []
  )

  const scrollToBottom = () => {
    if (refWrap.current) {
      refWrap.current.scrollTo({ top: refWrap.current.scrollHeight })
    }
  }

  const scrollToMessage = (targetMessageId: string) => {
    if (!refWrap.current) return false

    const messageElement = document.getElementById(targetMessageId)

    if (messageElement) {
      const containerRect = refWrap.current.getBoundingClientRect()
      const messageRect = messageElement.getBoundingClientRect()
      const scrollTop =
        refWrap.current.scrollTop +
        messageRect.top -
        containerRect.top -
        containerRect.height / 2 +
        messageRect.height / 2

      debouncedScrollToMessage(() => {
        if (refWrap.current) refWrap.current.scrollTo({ top: scrollTop, behavior: 'smooth' })
      })
    }

    scrollToBottom()
  }

  useEffect(() => {
    if (scrollBottomTrigger) {
      scrollToBottom()
      return
    }
  }, [scrollBottomTrigger])

  useEffect(() => {
    if (!messageId || loading || hasScrolledToMessage) return

    scrollToMessage(String(messageId))
  }, [mediaLoadedTrigger, messageId, loading, hasScrolledToMessage, debouncedScrollToMessage])

  useEffect(() => {
    if (messageId) {
      debouncedScrollToMessage.cancel()
      setHasScrolledToMessage(false)
    }
  }, [messageId, debouncedScrollToMessage])

  useEffect(() => {
    return () => {
      debouncedScrollToMessage.cancel()
    }
  }, [debouncedScrollToMessage])

  useEventMessageUpdatedNew(store)
  useEventMessageReceivedNew(store)
  useEventVoicemailReceived(store)
  useEventMessageTranscriptCompleted(store)
  useEventMessageTranscriptStarted(store)
  useEventMessageTranscriptFailed(store)
  useEventConversationActivityCreated(store)
  useEventTyping(store)
  useEventViewing(store)

  const isActiveTab = useIsTabActive()

  useEffect(() => {
    setIsActiveTab(isActiveTab)
  }, [isActiveTab])

  const handleLoadMore = async () => {
    const prevScrollHeight = refWrap.current?.scrollHeight || 0

    await loadPreviousMessages()

    const currentScrollHeight = refWrap.current?.scrollHeight || 0
    const prevScrollPosition = currentScrollHeight - prevScrollHeight

    if (prevScrollPosition) refWrap.current?.scrollTo({ top: prevScrollPosition })
  }

  const handleSetVisibleArrowBottom = () => {
    if (!refWrap.current) return

    const scrollHeight = refWrap.current.scrollHeight
    const scrollPosition = refWrap.current.clientHeight + refWrap.current.scrollTop
    const scrollFromBottom = scrollHeight - scrollPosition

    setVisibleArrowBottom(scrollFromBottom > SHOW_ARROW_BOTTOM)
  }

  const onScroll = (e: UIEvent<HTMLDivElement>) => {
    if (e.target instanceof Element) {
      const scrollHeight = e.target?.scrollHeight
      const scrollPosition = e.target?.clientHeight + e.target?.scrollTop
      const scrollFromBottom = scrollHeight - scrollPosition

      handleSetVisibleArrowBottom()

      if (messagesContentScrollHeight && messagesContentScrollHeight === scrollHeight) {
        setIsBottomFixed(scrollFromBottom === 0)
      }
      setMessagesContentScrollHeight(scrollHeight)
    }
  }

  const onClickScrollToBottom = async () => {
    await store.loadMessagesAllNext()

    scrollToBottom()

    if (!store.isRead) {
      conversationStore.onRead(store.conversationId)
    }
  }

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setHideAvatars(entry.borderBoxSize?.[0].inlineSize <= 404)
      }
    })
    const mutationObserver = new MutationObserver(() => {
      handleSetVisibleArrowBottom()
    })

    if (refWrap.current) {
      resizeObserver.observe(refWrap.current)
      mutationObserver.observe(refWrap.current, {
        childList: true,
        subtree: true,
        characterData: true,
      })
    }

    return () => {
      if (refWrap.current) {
        resizeObserver.unobserve(refWrap.current)
      }

      mutationObserver.disconnect()
    }
  }, [])

  const renderItems = (items: (Message | Activity)[], isScheduled?: boolean) => {
    return items.map((item, index) => (
      <DayContent
        key={item.id}
        isHideDay={!!index && item.day === items[index - 1]?.day}
        day={item.day}
        items={[item]}
        isScheduled={isScheduled}
        conversationMessagesStore={store}
      />
    ))
  }

  const isShowLoading = hasPrevious || loading

  return (
    <div className={styles.content}>
      {loadingOverlay && (
        <div className={styles.spinnerOverlay}>
          <SpinnerLoader ariaLabel={'ConversationContentAll'} size={32} />
        </div>
      )}

      <ScrollToBottomBtn
        open={visibleArrowBottom && !loading}
        onClick={onClickScrollToBottom}
        primary={Boolean(isUnread)}
      />
      <div
        className={styles.wrap}
        ref={refWrap}
        onScroll={onScroll}
        aria-label={getAriaLabel('List of messages')}
      >
        <div style={{ marginTop: 'auto' }}></div>
        <EmptyState />
        <InView onChange={(inView) => inView && handleLoadMore()}>
          {({ ref }) => (
            <div ref={ref}>
              {isShowLoading && (
                <SpinnerLoader
                  ariaLabel={'ConversationContent'}
                  size={16}
                  className={styles.spinner}
                />
              )}
            </div>
          )}
        </InView>
        {renderItems(ordinaryItems)}
        {renderItems(scheduledItems, true)}
        {!!sendingMessages.length && (
          <div className={styles.warpSending} aria-label={getAriaLabel('Sending Message', 'wrap')}>
            {sendingMessages.map((message) => (
              <MessageCard
                key={message.client_id}
                message={message}
                selectedSearch={store.selectedSearch}
                setScrollBottomTrigger={setScrollBottomTrigger}
                callContentStore={callContentStore}
                conversationMessagesStore={store}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
})
